import type { ServerConfig } from "@ronin/contracts";
import { useQuery } from "@tanstack/react-query";
import { CheckField, FormSection } from "./form-section.js";
import { serverStatsQuery, shownStats } from "./server-widget.js";

/** spec14 FR8: which stats a server widget shows (at least one). */
export function ServerFields({
  value,
  onChange,
}: {
  value: ServerConfig;
  onChange: (config: ServerConfig) => void;
}) {
  const stats = useQuery(serverStatsQuery);
  const shown = shownStats(value);
  const disks = stats.data?.disks.map((d) => d.label) ?? [];
  const networkReady = stats.data ? stats.data.network !== null : false;

  const toggleDisk = (label: string, on: boolean) => {
    const current = value.disks ?? disks;
    onChange({
      ...value,
      disks: on
        ? [...current.filter((d) => d !== label), label]
        : current.filter((d) => d !== label),
    });
  };

  const options: Array<{
    key: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    hint?: string;
    set: (on: boolean) => void;
  }> = [
    { key: "cpu", label: "CPU", checked: shown.cpu, set: (on) => onChange({ ...value, cpu: on }) },
    {
      key: "memory",
      label: "Memory",
      checked: shown.memory,
      set: (on) => onChange({ ...value, memory: on }),
    },
    ...disks.map((label) => ({
      key: `disk-${label}`,
      label: `Storage: ${label}`,
      checked: shown.disk(label),
      set: (on: boolean) => toggleDisk(label, on),
    })),
    {
      key: "network",
      label: "Network",
      checked: shown.network && networkReady,
      disabled: !networkReady,
      hint: networkReady ? undefined : "Not set up on this server",
      set: (on) => onChange({ ...value, network: on }),
    },
    {
      key: "uptime",
      label: "Uptime",
      checked: shown.uptime,
      set: (on) => onChange({ ...value, uptime: on }),
    },
  ];

  return (
    <FormSection title="Show" hint="At least one. The widget lays them out to fit its size.">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3">
        {options.map((option) => (
          <CheckField
            key={option.key}
            label={option.label}
            checked={option.checked}
            disabled={option.disabled}
            hint={option.hint}
            onChange={option.set}
          />
        ))}
      </div>
    </FormSection>
  );
}

/** At least one stat must stay on (FR8). */
export function hasStats(config: ServerConfig, diskLabels: string[]): boolean {
  const shown = shownStats(config);
  return (
    shown.cpu ||
    shown.memory ||
    shown.network ||
    shown.uptime ||
    diskLabels.some((label) => shown.disk(label))
  );
}
